<template>
  <button
    v-bind="rootProps"
    :class="[
      'sb-button',
      `sb-button--${variant}`,
      {
        'sb-button--is-clear': isClear,
        'sb-button--disabled': isDisabled,
        'sb-button--small': size === 'small',
        'sb-button--full': isFullWidth,
        'sb-button--has-icon-left': icon,
        'sb-button--has-icon-right': iconRight,
        'sb-button--loading': isLoading,
        'sb-button--align-left': alignLeft,
      },
    ]"
    :aria-disabled="isDisabled || isLoading"
    :aria-label="descriptionTextId ? undefined : label"
    :aria-busy="isLoading"
    :aria-describedby="descriptionTextId"
    :aria-pressed="isPressed"
    :type="type"
    @click="handleClick"
  >
    <SbIcon
      v-if="icon"
      :name="icon"
      :color="iconColor"
      :is-filled="iconFilled"
      aria-hidden="true"
    />
    <span class="sb-button__label">
      <slot>{{ label }}</slot>
    </span>
    <SbIcon
      v-if="iconRight"
      :name="iconRight"
      :color="iconColor"
      aria-hidden="true"
    />
    <SbLoading
      v-if="isLoading"
      type="spinner"
      size="normal"
      aria-hidden="true"
    />
    <slot name="pin" />
  </button>
</template>

<script setup lang="ts">
import SbIcon from '../Icon'
import SbLoading from '../Loading'
import type { AvailableButtonTypes, AvailableButtonVariants } from './lib'
import type { AvailableSizes } from '../../utils'
import type { ButtonHTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import type { IconColor, IconName } from '../Icon'

export type ButtonProps = {
  label: string
  variant?: AvailableButtonVariants
  size?: AvailableSizes
  /** [type docs](https://www.w3schools.com/tags/att_button_type.asp) */
  type?: AvailableButtonTypes
  isDisabled?: boolean
  isLoading?: boolean
  /** button takes the full width of the container */
  isFullWidth?: boolean | null
  descriptionTextId?: string
  /** Icon name left of the label. You can use [lucide icons](https://lucide.dev/icons) or the the Storyblok icons */
  icon?: IconName
  /** Icon name right of the label. You can use [lucide icons](https://lucide.dev/icons) or custom Storyblok icons */
  iconRight?: IconName
  /** Icon Variant */
  iconFilled?: boolean
  /** Do not use this if not needed. Check if perhaps a different variant of the button might be better. */
  iconColor?: IconColor
  /** Align Button left to align with surrounding content. This is mainly for CLEAR button types, due to the lack of visible borders of button. */
  alignLeft?: boolean
  /** Button is in pressed state */
  isPressed?: boolean | undefined
  /** Clear Version of the current button variant (no change, if variant is already "clear") */
  isClear?: boolean
} & /* @vue-ignore */ ButtonHTMLAttributes

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'normal',
  type: 'submit',
  isDisabled: false,
  isLoading: false,
  isFullWidth: false,
  iconFilled: false,
  isPressed: undefined,
  isClear: false,
  descriptionTextId: undefined,
  icon: undefined,
  iconRight: undefined,
  iconColor: undefined,
})

const rootProps = reactiveOmit(props, [
  'label',
  'variant',
  'size',
  'type',
  'isDisabled',
  'isLoading',
  'isFullWidth',
  'descriptionTextId',
  'icon',
  'iconRight',
  'iconFilled',
  'iconColor',
  'alignLeft',
  'isPressed',
  'isClear',
])

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const handleClick = (event: MouseEvent): void => {
  if (!props.isDisabled && !props.isLoading) {
    emit('click', event)
  }
}

defineSlots<{
  /**
   * Adds a custom label. This can be a component or text.
   * Note:  It is not intended that you add a custom icon here.
   *        For Icon buttons, please use the SbIconButton.
   *        For Accessibility puposes you will still need to add a descriptive label using the "label" prop.
   */
  default(): unknown
  pin: (props: {}) => any
}>()
</script>
